import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiRotateCcw } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getUnlockedFinancialYears } from '../../utils/financialYears';
import {
  getAccountingLedgerGroups,
  getLedgerSubGroups,
  getAllLedgerAccountsWithInactive,
  createAccountingLedgerAccount,
  updateAccountingLedgerAccount,
  reactivateLedgerAccount,
  deleteAccountingLedgerAccount,
  permanentDeleteLedgerAccount,
} from '../../services/api';
import { fmtCurrency } from '../../utils/formatNumber';
import LedgerCategorySelect from '../../components/common/LedgerCategorySelect';
import styles from './LedgerPage.module.css';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

/** Block decimal point / exponent / sign keys so only whole numbers can be typed */
const blockNonInteger = (e) => {
  if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

/** Coerce any input to a whole-number string (truncates decimals, e.g. pasted "501.44" → "501") */
const toIntString = (v) => String(v ?? '').split('.')[0].replace(/[^\d]/g, '');

const GROUP_META = {
  asset:       { label: 'Assets'      },
  liability:   { label: 'Liabilities' },
  equity:      { label: 'Equity'      },
  income:      { label: 'Income'      },
  expenditure: { label: 'Expenses'    },
};

const defaultNatureForGroup = (nature) =>
  nature === 'asset' || nature === 'expenditure' ? 'debit' : 'credit';

const EMPTY = { ledgerGroupId: '', subGroupId: '', name: '', description: '', openingYearId: '', openingBalanceAmountInr: '', openingBalanceNature: '' };

const LedgerPage = () => {
  const { activeDerasarId, financialYears } = useAuth();
  const unlockedYears = getUnlockedFinancialYears(financialYears || []);

  const [groups,       setGroups]       = useState([]);
  const [allSGs,       setAllSGs]       = useState([]);
  const [accounts,     setAccounts]     = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [search,       setSearch]       = useState('');
  const [filterNature, setFilterNature] = useState('all');
  const [filterActive, setFilterActive] = useState('active'); // 'active' | 'inactive' | 'all'
  const [loading,      setLoading]      = useState(true);
  const [modal,        setModal]        = useState(null);
  const [delTarget,    setDelTarget]    = useState(null);
  const [saving,       setSaving]       = useState(false);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const [gRes, sgRes, aRes] = await Promise.all([
        getAccountingLedgerGroups(activeDerasarId),
        getLedgerSubGroups(activeDerasarId),
        getAllLedgerAccountsWithInactive(activeDerasarId),
      ]);
      setGroups(gRes.data.sort((a, b) => a.displayOrder - b.displayOrder));
      setAllSGs(sgRes.data);
      setAccounts(aRes.data);
    } catch {
      toast.error('Failed to load ledger accounts');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    let pool = accounts;
    if (filterActive === 'active')   pool = pool.filter(a => a.isActive !== false);
    if (filterActive === 'inactive') pool = pool.filter(a => a.isActive === false);
    if (filterNature !== 'all')      pool = pool.filter(a => a.group?.nature === filterNature);
    if (q) pool = pool.filter(a =>
      a.name.toLowerCase().includes(q) ||
      (a.description || '').toLowerCase().includes(q) ||
      (a.accountCode || '').toLowerCase().includes(q) ||
      (a.subGroup?.name || '').toLowerCase().includes(q)
    );
    setFiltered(pool);
  }, [search, accounts, filterNature, filterActive]);

  const categoryOptions = useMemo(
    () => allSGs.map(sg => {
      const grp = groups.find(g => String(g.id) === String(sg.ledgerGroupId));
      return {
        value: sg.id,
        label: sg.name,
        groupNature: grp?.nature,
        groupLabel: grp ? (GROUP_META[grp.nature]?.label || grp.name) : '',
      };
    }),
    [allSGs, groups],
  );

  const patch = (field, value) =>
    setModal(m => {
      const next = { ...m.data, [field]: value };
      if (field === 'subGroupId') {
        // Group is implied by the chosen category; derive it for the opening-balance nature.
        const sg  = allSGs.find(s => String(s.id) === String(value));
        const grp = sg ? groups.find(g => String(g.id) === String(sg.ledgerGroupId)) : null;
        next.ledgerGroupId = grp ? String(grp.id) : '';
        next.openingBalanceNature = grp?.normalBalance === 'Cr' ? 'credit' : 'debit';
      }
      return { ...m, data: next };
    });

  const openAdd  = () => setModal({ mode: 'add',  data: { ...EMPTY } });
  const openEdit = (acc) => setModal({ mode: 'edit', data: {
    id:                      acc.id,
    name:                    acc.name,
    description:             acc.description || '',
    isSystem:                acc.isSystem,
    ledgerGroupId:           acc.ledgerGroupId || '',
    subGroupId:              acc.subGroupId    || '',
    openingYearId:           acc.openingBalance?.financialYearId ? String(acc.openingBalance.financialYearId) : '',
    openingYearLabel:        acc.openingBalance?.yearLabel || '',
    hasExistingOB:           !!acc.openingBalance,
    openingBalanceAmountInr: acc.openingBalance?.amountInr != null ? String(acc.openingBalance.amountInr) : '',
    openingBalanceNature:    acc.openingBalance?.nature || defaultNatureForGroup(acc.group?.nature),
  }});

  const handleSave = async (e) => {
    e.preventDefault();
    if (modal.mode === 'add' && !modal.data.subGroupId) {
      toast.error('Please select a category');
      return;
    }
    if (modal.mode === 'edit' && parseFloat(modal.data.openingBalanceAmountInr) > 0 && !modal.data.openingYearId) {
      toast.error('Please select a financial year for the opening balance');
      return;
    }
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        await createAccountingLedgerAccount({
          derasarId:                activeDerasarId,
          subGroupId:               modal.data.subGroupId,
          name:                     modal.data.name,
          description:              modal.data.description || undefined,
          openingYearId:            modal.data.openingYearId || undefined,
          openingBalanceAmountInr:  modal.data.openingYearId ? (modal.data.openingBalanceAmountInr || 0) : undefined,
          openingBalanceNature:     modal.data.openingYearId ? (modal.data.openingBalanceNature || 'debit') : undefined,
        });
        toast.success('Ledger account created');
      } else {
        const updatePayload = {
          description:             modal.data.description,
          openingYearId:           modal.data.openingYearId || null,
          openingBalanceAmountInr: modal.data.openingYearId ? (parseFloat(modal.data.openingBalanceAmountInr) || 0) : undefined,
          openingBalanceNature:    modal.data.openingYearId ? (modal.data.openingBalanceNature || 'debit') : undefined,
        };
        if (!modal.data.isSystem) {
          updatePayload.name = modal.data.name;
        }
        await updateAccountingLedgerAccount(modal.data.id, updatePayload);
        toast.success('Ledger account updated');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await deleteAccountingLedgerAccount(delTarget.id);
      toast.success('Account deactivated');
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate');
    }
  };

  const handlePermDelete = async () => {
    try {
      await permanentDeleteLedgerAccount(delTarget.id);
      toast.success('Account permanently deleted');
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleReactivate = async (acc) => {
    try {
      await reactivateLedgerAccount(acc.id);
      toast.success(`${acc.name} reactivated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reactivate');
    }
  };

  const inactiveCount = accounts.filter(a => a.isActive === false).length;

  if (!activeDerasarId)
    return <div className="empty-state"><p>Select a derasar to manage ledger accounts</p></div>;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <div>
          <h2>Ledger Accounts</h2>
          <p className={styles.subtitle}>All accounts across Groups → Categories → Ledger</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openAdd}>
          <FiPlus /> New Ledger
        </button>
      </div>

      <div className={styles.toolbar}>
        {/* Active / Inactive toggle */}
        <div className={styles.tabs} style={{ marginRight: 8 }}>
          {[
            { key: 'active',   label: 'Active' },
            { key: 'inactive', label: `Inactive${inactiveCount > 0 ? ` (${inactiveCount})` : ''}` },
            { key: 'all',      label: 'All' },
          ].map(t => (
            <button
              key={t.key}
              className={`${styles.tab} ${filterActive === t.key ? styles.tabActive : ''}`}
              onClick={() => setFilterActive(t.key)}
              style={t.key === 'inactive' && inactiveCount > 0 ? { color: filterActive === t.key ? undefined : 'var(--color-danger)' } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Group filter tabs */}
        <div className={styles.tabs}>
          {[
            { key: 'all',         label: 'All Groups' },
            { key: 'asset',       label: 'Assets' },
            { key: 'liability',   label: 'Liabilities' },
            { key: 'equity',      label: 'Equity' },
            { key: 'income',      label: 'Income' },
            { key: 'expenditure', label: 'Expenses' },
          ].map(t => (
            <button
              key={t.key}
              className={`${styles.tab} ${filterNature === t.key ? styles.tabActive : ''}`}
              onClick={() => setFilterNature(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            placeholder="Search name, code, category…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Ledger Name</th>
                  <th>Category</th>
                  <th>Group</th>
                  <th>Description</th>
                  <th style={{ width: 130 }}>Opening Balance</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        {search
                          ? 'No accounts match your search.'
                          : filterActive === 'inactive'
                            ? 'No deactivated accounts.'
                            : 'No ledger accounts yet. Click "New Ledger" to create one.'}
                      </div>
                    </td>
                  </tr>
                )}
                {filtered.map((acc, i) => {
                  const isInactive = acc.isActive === false;
                  const nature = acc.group?.nature;
                  const groupLabel = GROUP_META[nature]?.label || acc.group?.name || '—';
                  const categoryLabel = acc.subGroup?.name || '—';
                  return (
                    <tr
                      key={acc.id}
                      style={isInactive ? { opacity: 0.6 } : undefined}
                    >
                      <td className={styles.idx}>{i + 1}</td>
                      <td className={styles.nameCell}>
                        {acc.name}
                        {acc.isSystem && <span className={styles.sysTag}>system</span>}
                        {isInactive && (
                          <span className="badge badge-rejected" style={{ fontSize: '0.68rem', marginLeft: 6 }}>
                            inactive
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge badge-pending" style={{ fontSize: '0.72rem' }}>
                          {categoryLabel}
                        </span>
                      </td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                        {groupLabel}
                      </td>
                      <td className={styles.desc}>{acc.description || '—'}</td>
                      <td>
                        {acc.openingBalance ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                              <span style={{ color: acc.openingBalance.nature === 'credit' ? 'var(--color-danger)' : 'var(--color-success)', marginRight: 4 }}>
                                {acc.openingBalance.nature === 'credit' ? 'Cr' : 'Dr'}
                              </span>
                              {fmtCurrency(acc.openingBalance.amountInr)}
                            </span>
                            {acc.openingBalance.yearLabel && (
                              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                                {acc.openingBalance.yearLabel}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>—</span>
                        )}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          {!isInactive && (
                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(acc)} title="Edit">
                              <FiEdit2 size={14} />
                            </button>
                          )}
                          {!isInactive && !acc.isSystem && !acc.isDonationLinked && (
                            <button className="btn btn-danger btn-sm" onClick={() => setDelTarget(acc)} title="Deactivate / Delete">
                              <FiTrash2 size={14} />
                            </button>
                          )}
                          {isInactive && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleReactivate(acc)}
                              title="Reactivate"
                              style={{ color: 'var(--color-success)' }}
                            >
                              <FiRotateCcw size={14} /> Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className={`modal ${styles.wideModal}`} onClick={e => e.stopPropagation()}>
            <div className="card-header">
              <h3>{modal.mode === 'add' ? 'New Ledger Account' : 'Edit Ledger Account'}</h3>
            </div>
            <form onSubmit={handleSave}>
              <div className={styles.modalBody}>

                <div className="form-group">
                  <label>Ledger Name *</label>
                  <input
                    required
                    autoFocus
                    value={modal.data.name}
                    readOnly={modal.data.isSystem}
                    style={modal.data.isSystem ? { background: 'var(--color-surface-2)', cursor: 'default' } : undefined}
                    onChange={e => !modal.data.isSystem && patch('name', e.target.value)}
                    placeholder="e.g., HDFC Current Account"
                  />
                </div>

                {modal.mode === 'add' && (
                  <div className="form-group">
                    <label>Category *</label>
                    <LedgerCategorySelect
                      value={modal.data.subGroupId}
                      onChange={(id) => patch('subGroupId', id)}
                      options={categoryOptions}
                      placeholder="Select category…"
                    />
                  </div>
                )}

                {modal.mode === 'edit' && (
                  <p className={styles.hint}>Group and category cannot be changed after creation.</p>
                )}

                <div className="form-group">
                  <label>Description</label>
                  <input
                    value={modal.data.description}
                    onChange={e => patch('description', e.target.value)}
                    placeholder="Optional"
                  />
                </div>

                {modal.mode === 'edit' && (
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
                      Opening Balance
                    </div>
                    {modal.data.hasExistingOB && (
                      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0 0 8px' }}>
                        Year is fixed once an opening balance is set. Select None to remove it, then re-add with the correct year.
                      </p>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: '0 12px' }}>
                      <div className="form-group">
                        <label>Year</label>
                        <select
                          value={modal.data.openingYearId || ''}
                          onChange={e => { patch('openingYearId', e.target.value); if (!e.target.value) patch('openingBalanceAmountInr', ''); }}
                          style={{ width: 110 }}
                        >
                          <option value="">None</option>
                          {unlockedYears.map(y => <option key={y.id} value={y.id}>{y.yearLabel}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Nature</label>
                        <select value={modal.data.openingBalanceNature || 'debit'} onChange={e => patch('openingBalanceNature', e.target.value)} style={{ width: 70 }}>
                          <option value="debit">Dr</option>
                          <option value="credit">Cr</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Amount (₹)</label>
                        <input
                          type="number" min="0" step="1"
                          className={styles.noSpin} onWheel={blockWheel}
                          onKeyDown={blockNonInteger}
                          value={modal.data.openingBalanceAmountInr}
                          onChange={e => patch('openingBalanceAmountInr', toIntString(e.target.value))}
                          placeholder="0"
                          disabled={!modal.data.openingYearId}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {modal.mode === 'add' && unlockedYears.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0 12px' }}>
                    <div className="form-group">
                      <label>Opening Year <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}>(optional)</em></label>
                      <select value={modal.data.openingYearId} onChange={e => { patch('openingYearId', e.target.value); if (!e.target.value) patch('openingBalanceAmountInr', ''); }}>
                        <option value="">None</option>
                        {unlockedYears.map(y => <option key={y.id} value={y.id}>{y.yearLabel}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Nature</label>
                      <select value={modal.data.openingBalanceNature || 'debit'} onChange={e => patch('openingBalanceNature', e.target.value)} style={{ width: 70 }}>
                        <option value="debit">Dr</option>
                        <option value="credit">Cr</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Opening Balance (₹) <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}>(optional)</em></label>
                      <input
                        type="number" min="0" step="1" placeholder="0"
                        className={styles.noSpin} onWheel={blockWheel}
                        onKeyDown={blockNonInteger}
                        value={modal.data.openingBalanceAmountInr}
                        onChange={e => patch('openingBalanceAmountInr', toIntString(e.target.value))}
                        disabled={!modal.data.openingYearId}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : modal.mode === 'add' ? 'Create' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate / Delete confirmation */}
      {delTarget && (
        <div className="modal-overlay" onClick={() => setDelTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header"><h3>Manage Account</h3></div>
            <div style={{ padding: '16px 20px' }}>
              <p>What would you like to do with <strong>{delTarget.name}</strong>?</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: '10px 0 0' }}>
                <strong>Deactivate</strong> hides the account — existing entries and balances are preserved.<br />
                <strong>Delete Permanently</strong> removes it forever and only works if no transactions exist.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDelTarget(null)}>Cancel</button>
              <button className="btn btn-ghost" onClick={handleDeactivate}>Deactivate</button>
              <button className="btn btn-danger" onClick={handlePermDelete}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerPage;
